import React, { useRef } from "react";
import { Button, ButtonToggle, ButtonToggleOption, Flyout, Icon, Text } from "@showitinc/showit-wc";

import Input from "../app2components/base/Input";
import When from "./utils/When";
import AppConstants from "../constants/AppConstants";
import SplitMergeWrapper from "../app2components/base/SplitMergeWrapper";
import { INPUT_TYPE_NUMBER } from "../constants/ElementConstants";
import { initFormatter } from "../utils/PropertyFormatters";
import styles from "./CornerStyleProperties.module.css";
import { isValueMixed } from "../utils/InheritanceUtils";
import { MIXED } from "../constants/DesignConstants.mjs";

const {
  BORDER_CIRCLE_RAD,
  CORNER_INDEX,
  CORNER_PRESET_LABEL_CURVED_CORNER,
  CORNER_PRESET_LABEL_CURVED_SIDE,
  CORNER_PRESET_LABEL_OPPOSITE_CORNER,
  CORNER_PRESET_LABEL_SHARP_CORNER,
  CORNER_STYLE_OPTION_ELLIPSE,
  CORNER_STYLE_OPTION_NONE,
  CORNER_STYLE_OPTION_ROUND,
  INPUT_SOURCE_PROPERTY_INPUT,
  UPDATE_ACTION_DELETE,
  UPDATE_ACTION_SET,
} = AppConstants;

const { TR, TL, BR, BL } = CORNER_INDEX;

const presetData = {
  [CORNER_PRESET_LABEL_CURVED_SIDE]: {
    curvedTop: [TL, TR],
    curvedBottom: [BR, BL],
    curvedRight: [TR, BR],
    curvedLeft: [TL, BL],
  },
  [CORNER_PRESET_LABEL_SHARP_CORNER]: {
    sharpTopLeft: [TR, BR, BL],
    sharpTopRight: [TL, BR, BL],
    sharpBottomRight: [TL, TR, BL],
    sharpBottomLeft: [TL, TR, BR],
  },
  [CORNER_PRESET_LABEL_CURVED_CORNER]: {
    curvedTopLeft: [TL],
    curvedTopRight: [TR],
    curvedBottomRight: [BR],
    curvedBottomLeft: [BL],
  },
  [CORNER_PRESET_LABEL_OPPOSITE_CORNER]: {
    oppLeft: [TR, BL],
    oppRight: [TL, BR],
  },
};

const cornerData = [
  {
    corner: TL,
    icon: "cornerTopLeft",
  },
  {
    corner: TR,
    icon: "cornerTopRight",
  },
  {
    corner: BL,
    icon: "cornerBottomLeft",
  },
  {
    corner: BR,
    icon: "cornerBottomRight",
  },
];

// Flatten the presetData structure to get all icons and their corresponding corner values
const flattenedPresetData = Object.entries(presetData).flatMap(([label, set]) =>
  Object.entries(set).map(([type, corners]) => ({ type, corners }))
);

const splitCorners = (borderRadius) => {
  const brValues = String(borderRadius)
    .split(" ")
    .map((brCorner) => (brCorner === CORNER_STYLE_OPTION_NONE ? 0 : brCorner));

  return brValues.length === 1
    ? [...brValues, ...Array(3).fill(brValues[0])]
    : brValues.length === 2
    ? [...brValues, ...brValues]
    : brValues;
};

const CornerStyleProperties = ({
  isLocked,
  layout,
  length,
  mixedSync,
  onPropUpdate,
  propName,
  sync,
  toggleSync,
  usePresets,
  value: borderRadius,
  visiblePropMap,
  onlyShowDesktop = false,
  removeCustomization,
}) => {
  const inputRef = useRef();
  const flyoutRef = useRef();
  const openPanel = () => flyoutRef.current.showPopover(inputRef.current);

  const borderRadiusSelection =
    borderRadius === MIXED
      ? borderRadius
      : borderRadius === BORDER_CIRCLE_RAD
      ? CORNER_STYLE_OPTION_ELLIPSE
      : borderRadius === CORNER_STYLE_OPTION_NONE
      ? CORNER_STYLE_OPTION_NONE
      : CORNER_STYLE_OPTION_ROUND;

  const distinctCorners =
    borderRadiusSelection === CORNER_STYLE_OPTION_ROUND &&
    new Set(splitCorners(borderRadius)).size > 1;

  const isEllipse = borderRadiusSelection === CORNER_STYLE_OPTION_ELLIPSE;

  const _onCornerValueChange =
    (corner, addUndo = true) =>
    ({ detail }) => {
      if (borderRadiusSelection === CORNER_STYLE_OPTION_ELLIPSE) return;

      const { value } = detail;
      const brValues = splitCorners(borderRadius);
      brValues[corner] = value || 0;

      const allCornersSame = brValues.every((val) => Number(val) === Number(brValues[0]));
      const distinctCheckValue = allCornersSame ? Number(brValues[0]) : brValues.join(" ");

      onPropUpdate({
        name: propName,
        value: distinctCheckValue,
        addUndo,
        source: INPUT_SOURCE_PROPERTY_INPUT,
      });
    };

  const reset = () => {
    onPropUpdate({
      action: UPDATE_ACTION_DELETE,
      name: propName,
    });
  };

  /**
   * Generates a function to handle preset corner selections.
   *
   * `_onPresetSelection` is a higher-order function that, when invoked with a set of corners,
   * returns a new function that, when called, will adjust the corner values based on the provided corners
   * and trigger a property change event.
   *
   * The corner values are determined based on the length of the `corners` array:
   * - If there's only one corner, the value will be `length`.
   * - If there are multiple corners, the value will be `length / 2`.
   *
   * The resulting function, when invoked, will:
   * 1. Create a default array of corner values, initialized to zeros.
   * 2. Set the calculated value (based on the number of corners) to the specified positions in the array.
   * 3. Call `_onPropChange` with the adjusted corner values.
   *
   * @function
   * @param {number[]} corners - An array of indices representing the corners to adjust (e.g., [0, 2] could represent top-left and bottom-left corners).
   * @returns {Function} A function that, when invoked, will adjust the corner values and trigger a property change event.
   *
   * @example
   * clickAction={_onPresetSelection(corners)}
   */
  const _onPresetSelection = (corners) => () => {
    let cornerValues = Array(4).fill(0);

    if (corners === CORNER_STYLE_OPTION_NONE) {
      cornerValues = [0];
    } else if (corners === CORNER_STYLE_OPTION_ELLIPSE) {
      cornerValues = isEllipse ? [0] : [BORDER_CIRCLE_RAD];
    } else if (corners === CORNER_STYLE_OPTION_ROUND) {
      cornerValues = [10];
    } else {
      const roundedCornerValue = corners.length === 1 ? length : length / 2;
      corners.forEach((corner) => {
        cornerValues[corner] = roundedCornerValue;
      });
    }

    if (cornerValues.every((corner) => corner === 0)) {
      reset();
    } else {
      onPropUpdate({
        name: propName,
        value: Array.isArray(cornerValues) ? cornerValues.join(" ") : cornerValues,
      });
    }
  };

  const _onCornersUpdate = (propInfo) => {
    const { value } = propInfo;

    onPropUpdate({
      ...propInfo,
      action: value === 0 ? UPDATE_ACTION_DELETE : UPDATE_ACTION_SET,
    });
  };

  const parsedBorderRadius = splitCorners(borderRadius).map((entry) => Math.round(Number(entry)));
  const { onChange: onCornersChange } = initFormatter(_onCornersUpdate);

  return (
    <SplitMergeWrapper
      layout={layout}
      sync={sync || !layout}
      toggleSync={toggleSync}
      mixed={mixedSync}
      removeCustomization={removeCustomization}
    >
      <ButtonToggle
        label="Corners"
        clickOnly
        context={!isValueMixed(borderRadiusSelection) && !mixedSync}
        disabled={isLocked}
      >
        <ButtonToggleOption
          data-testid="corners-none-button"
          checked={borderRadiusSelection === CORNER_STYLE_OPTION_NONE}
          icon="rectangle"
          onSIToggle={_onPresetSelection(CORNER_STYLE_OPTION_NONE, CORNER_STYLE_OPTION_NONE)}
          disabled={isLocked}
        />
        <ButtonToggleOption
          data-testid="corners-round-button"
          checked={borderRadiusSelection === CORNER_STYLE_OPTION_ROUND}
          icon="roundCorners"
          onSIToggle={_onPresetSelection(CORNER_STYLE_OPTION_ROUND, CORNER_STYLE_OPTION_ROUND)}
          disabled={isLocked}
        />
        <ButtonToggleOption
          data-testid="corners-ellipse-button"
          checked={borderRadiusSelection === CORNER_STYLE_OPTION_ELLIPSE}
          icon="ellipse"
          onSIToggle={_onPresetSelection(CORNER_STYLE_OPTION_ELLIPSE, CORNER_STYLE_OPTION_ELLIPSE)}
          disabled={isLocked}
        />
      </ButtonToggle>
      {borderRadiusSelection === CORNER_STYLE_OPTION_ROUND && (
        <>
          <Input
            data-testid="corner-radius-input"
            disabled={isLocked}
            min="0"
            label="Corner radius"
            type={INPUT_TYPE_NUMBER}
            onSIBeforeChange={onCornersChange(propName, borderRadius)}
            onSIInput={onCornersChange(propName, borderRadius, false)}
            units="px"
            mixed={distinctCorners && !isEllipse}
            value={distinctCorners ? MIXED : !isEllipse ? parsedBorderRadius[0] || 0 : 0}
            ref={inputRef}
          >
            <Button
              slot="suffix"
              variant="icon"
              iconPrefix="cornerRadius"
              onSIClick={openPanel}
              disabled={isLocked}
            />
          </Input>

          <Flyout ref={flyoutRef} position="top" flip="false">
            <div slot="header-content" className={styles.header}>
              <Text variantStyle="bold">Corner Radius</Text>
            </div>
            <div className={`${styles["corner-radius-flyout"]}`}>
              {cornerData.map(({ icon, corner }, index) => (
                <Input
                  key={corner}
                  type={INPUT_TYPE_NUMBER}
                  enableSuffixDrag
                  min="0"
                  disabled={isLocked || isEllipse}
                  step="1"
                  value={!isEllipse ? parsedBorderRadius[corner] || 0 : 0}
                  onSIBeforeChange={_onCornerValueChange(corner)}
                  onSIInput={_onCornerValueChange(corner, false)}
                  blurOnEnter
                  blurOnEscape
                  emitOnBlur
                  className={index === 1 || index === 3 ? styles.suffixOnly : ""}
                >
                  <Icon
                    className={`${styles["corner-icon"]} ${
                      index !== 1 && index !== 3 ? styles.labelOnly : ""
                    }`}
                    type={icon}
                    slot={index === 1 || index === 3 ? "suffix" : "label"}
                    size="12"
                  />
                </Input>
              ))}
            </div>
            <When it={usePresets}>
              <div className={styles.presets}>
                <div className={styles["presets-header"]}>
                  <Text variantStyle="bold">Presets</Text>
                </div>
                <div className={styles.presetOptions}>
                  {flattenedPresetData.map(({ type, corners }) => (
                    <ButtonToggleOption
                      key={type}
                      className={styles["preset-option"]}
                      icon={type}
                      onSIToggle={_onPresetSelection(corners)}
                      checked={0}
                      disabled={isLocked}
                    />
                  ))}
                </div>
              </div>
            </When>
          </Flyout>
        </>
      )}
    </SplitMergeWrapper>
  );
};

export default CornerStyleProperties;
